/**
 * 推送通知点击 → 配货页 Replenishment 视频弹窗
 *
 * 服务端极光 extras 建议（任选其一）：
 * 1) { "type": "video", "productId": "123", "videoIndex": "0" }
 * 2) { "type": "product", "productId": "123" }
 * 3) { "xpzOpenVideo": "1", "xpzVideoProductId": "123", "xpzVideoIndex": "0" }
 * 4) 极光控制台常见小写：{ "type": "video", "productid": "123", "videoIndex": "0" }
 * 5) 搜索推送：{ "type": "search", "searchKeyword": "可选，不传则用 notification.title" }
 */

import { Capacitor } from '@capacitor/core';

export const PUSH_PENDING_VIDEO_KEY = 'xpz_pending_push_video';
export const PUSH_PENDING_SEARCH_KEY = 'xpz_pending_push_search';
const REPLENISHMENT_PATH = '/replenishment';

/** DOM Event 自带字段，不能当作推送 extras */
const DOM_EVENT_RESERVED_KEYS = new Set([
  'type',
  'target',
  'currentTarget',
  'eventPhase',
  'bubbles',
  'cancelable',
  'defaultPrevented',
  'timeStamp',
  'isTrusted',
  'composed',
  'cancelBubble',
  'returnValue',
  'srcElement',
  'detail',
]);

const NESTED_EXTRA_KEYS = [
  'cn.jpush.android.EXTRA',
  'cn.jpush.android.extra',
  'extras',
  'extra',
  'additionalData',
  'data',
  'payload',
  'custom',
];

/** JPush 5.x 标准附加字段 key */
const JPUSH_EXTRA_KEY = 'cn.jpush.android.EXTRA';

function getJPushCachedOpenNotification() {
  if (typeof window === 'undefined') return null;
  const candidates = [
    window.JPush?.openNotification,
    window.plugins?.jPushPlugin?.openNotification,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const item = candidates[i];
    if (item && typeof item === 'object' && Object.keys(item).length > 0) {
      return item;
    }
  }
  return null;
}

function enumerateOwnProps(obj) {
  const result = {};
  if (!obj || typeof obj !== 'object') return result;

  Object.keys(obj).forEach((key) => {
    result[key] = obj[key];
  });

  // Cordova 往 Event 上挂自定义字段时，部分 WebView 里 Object.keys 枚举不到
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = obj[key];
    }
  }

  return result;
}

function safeParseJson(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

/**
 * 解析 Java Map.toString() 格式：{productId=123, type=video, videoIndex=0}
 * JPush Android 插件常把 cn.jpush.android.EXTRA 转成这种字符串而非 JSON
 */
function parseJavaMapString(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;

  const inner = trimmed.slice(1, -1).trim();
  if (!inner) return {};

  const result = {};
  inner.split(/,\s+/).forEach((part) => {
    const eqIdx = part.indexOf('=');
    if (eqIdx <= 0) return;
    const key = part.slice(0, eqIdx).trim();
    const val = part.slice(eqIdx + 1).trim();
    if (key) result[key] = val;
  });

  return Object.keys(result).length > 0 ? result : null;
}

function parseExtraValue(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value;

  const json = safeParseJson(value);
  if (json && typeof json === 'object') return json;

  return parseJavaMapString(value);
}

function pickString(obj, keys) {
  if (!obj) return '';
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj[k];
    if (v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return '';
}

/** 极光 iOS 控制台附加字段常为全小写 productid / videoindex */
function pickStringCaseInsensitive(obj, keys) {
  const direct = pickString(obj, keys);
  if (direct) return direct;
  if (!obj || typeof obj !== 'object') return '';
  const wanted = new Set(keys.map((k) => String(k).toLowerCase()));
  const entries = enumerateOwnProps(obj);
  for (let i = 0; i < Object.keys(entries).length; i += 1) {
    const key = Object.keys(entries)[i];
    if (!wanted.has(String(key).toLowerCase())) continue;
    const v = entries[key];
    if (v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return '';
}

function normalizeExtraMap(value) {
  const parsed = parseExtraValue(value);
  return parsed && typeof parsed === 'object' ? parsed : null;
}

function collectPushExtraMaps(source) {
  const maps = [];
  if (!source) return maps;

  const push = (value) => {
    const map = normalizeExtraMap(value);
    if (map && typeof map === 'object') {
      maps.push(map);
    }
  };

  const absorb = (raw) => {
    if (!raw || typeof raw !== 'object') return;

    if (raw.detail != null) {
      absorb(raw.detail);
    }

    NESTED_EXTRA_KEYS.forEach((key) => {
      if (raw[key] != null) {
        push(raw[key]);
      }
    });

  // JPush 5.9：自定义字段在 cn.jpush.android.EXTRA
    if (raw[JPUSH_EXTRA_KEY] != null) {
      push(raw[JPUSH_EXTRA_KEY]);
    }

    const flat = enumerateOwnProps(raw);
    Object.keys(flat).forEach((key) => {
      if (DOM_EVENT_RESERVED_KEYS.has(key)) return;
      if (NESTED_EXTRA_KEYS.includes(key)) return;
      if (key === JPUSH_EXTRA_KEY) return;
      flat[key] = raw[key];
    });
    if (Object.keys(flat).length > 0) {
      maps.push(flat);
    }

    ['alert', 'message', 'content', 'body', 'title'].forEach((key) => {
      if (typeof raw[key] === 'string') {
        push(raw[key]);
      }
    });

    const visit = (obj, depth = 0) => {
      if (!obj || typeof obj !== 'object' || depth > 5) return;
      Object.keys(enumerateOwnProps(obj)).forEach((key) => {
        const val = obj[key];
        if (typeof val === 'string') {
          const parsed = parseExtraValue(val);
          if (parsed && typeof parsed === 'object') {
            maps.push(parsed);
          }
        } else if (val && typeof val === 'object') {
          maps.push(val);
          visit(val, depth + 1);
        }
      });
    };
    visit(raw, 0);
  };

  absorb(source);
  return maps;
}

function collectAllPushSources(primarySource) {
  const list = [];
  const seen = new Set();

  const add = (item) => {
    if (!item || typeof item !== 'object') return;
    try {
      const key = JSON.stringify(item);
      if (seen.has(key)) return;
      seen.add(key);
      list.push(item);
    } catch (e) {
      list.push(item);
    }
  };

  add(primarySource);
  add(getJPushCachedOpenNotification());

  if (primarySource?.detail) {
    add(primarySource.detail);
  }

  return list;
}

function mergeExtraMaps(maps) {
  const merged = {};
  maps.forEach((map) => {
    Object.keys(map).forEach((key) => {
      if (map[key] != null && merged[key] == null) {
        merged[key] = map[key];
      }
    });
  });
  return merged;
}

/**
 * 从 JPush 事件 / 原生 Intent / 普通对象解析视频跳转参数
 */
export function parsePushVideoPayload(source) {
  if (!source) return null;

  const sources = collectAllPushSources(source);
  let extras = {};
  sources.forEach((item) => {
    extras = { ...extras, ...mergeExtraMaps(collectPushExtraMaps(item)) };
  });

  const type = pickStringCaseInsensitive(extras, ['type', 'push_type', 'pageType']).toLowerCase();
  const productId = pickStringCaseInsensitive(extras, [
    'productId',
    'product_id',
    'productid',
    'push_id',
    'xpzVideoProductId',
    'xpzvideoproductid',
    'videoId',
    'video_id',
    'videoid',
  ]);
  const videoIndex = pickStringCaseInsensitive(extras, ['videoIndex', 'video_index', 'videoindex', 'xpzVideoIndex', 'xpzvideoindex', 'index']) || '0';
  const xpzOpenVideo = pickStringCaseInsensitive(extras, ['xpzOpenVideo', 'xpzopenvideo', 'openVideo', 'openvideo']);

  // 有 productId 即视为视频推送（避免与 DOM event.type = jpush.openNotification 冲突）
  const wantsVideo =
    !!productId &&
    type !== 'order' &&
    (
      type === 'video' ||
      type === 'product' ||
      type === '' ||
      xpzOpenVideo === '1' ||
      xpzOpenVideo === 'true' ||
      !!pickStringCaseInsensitive(extras, ['xpzVideoProductId', 'xpzvideoproductid'])
    );

  if (!wantsVideo || !productId) {
    console.log('[PushNav] parsePushVideoPayload: ignored', {
      type,
      productId,
      keys: Object.keys(extras),
      extras,
    });
    return null;
  }

  return {
    productId,
    videoIndex,
    xpzOpenVideo: '1',
    xpzVideoProductId: productId,
  };
}

/** iOS APNS：关键词常在 aps.alert 字符串或 { title, body } 中 */
function pickIosApnsAlert(source) {
  if (!source || typeof source !== 'object') return '';
  const aps = source.aps;
  if (!aps || typeof aps !== 'object') return '';

  const alert = aps.alert;
  if (typeof alert === 'string' && alert.trim()) {
    return alert.trim();
  }
  if (alert && typeof alert === 'object') {
    return (
      pickStringCaseInsensitive(alert, ['title', 'subtitle', 'body']) ||
      pickStringCaseInsensitive(alert, ['loc-key', 'loc_key'])
    );
  }
  return '';
}

function pickNotificationTitle(source) {
  if (!source || typeof source !== 'object') return '';
  const direct = pickStringCaseInsensitive(source, [
    'title',
    'notificationTitle',
    'android_title',
    'n_title',
    'alert',
  ]);
  if (direct) return direct;
  return pickIosApnsAlert(source);
}

function pickSearchKeywordFromExtras(extras) {
  return pickStringCaseInsensitive(extras, [
    'searchKeyword',
    'search_keyword',
    'searchkeyword',
    'search',
    'keyword',
    'keywords',
    'query',
    'searchParam',
    'searchparam',
  ]);
}

/**
 * 从 JPush 事件解析搜索跳转参数（推送标题填入搜索栏并自动搜索）
 * extras 建议：{ "type": "search", "searchKeyword": "搜索词（优先）；不传则用 notification.title" }
 */
export function parsePushSearchPayload(source) {
  if (!source) return null;
  if (parsePushVideoPayload(source)) return null;

  const sources = collectAllPushSources(source);
  let extras = {};
  sources.forEach((item) => {
    extras = { ...extras, ...mergeExtraMaps(collectPushExtraMaps(item)) };
  });

  const type = pickStringCaseInsensitive(extras, ['type', 'push_type', 'pageType']).toLowerCase();
  const explicitKeyword = pickSearchKeywordFromExtras(extras);
  const wantsSearch =
    type === 'search' ||
    type === 'product_search' ||
    !!explicitKeyword;

  if (!wantsSearch) {
    return null;
  }

  let keyword = explicitKeyword;
  if (!keyword) {
    for (let i = 0; i < sources.length; i += 1) {
      keyword = pickNotificationTitle(sources[i]) || pickIosApnsAlert(sources[i]);
      if (keyword) break;
    }
  }

  if (!keyword || !String(keyword).trim()) {
    console.log('[PushNav] parsePushSearchPayload: no keyword/title', {
      type,
      extras,
      sourceKeys: sources.map((s) => (s ? Object.keys(enumerateOwnProps(s)) : [])),
    });
    return null;
  }

  return {
    searchKeyword: String(keyword).trim(),
  };
}

export function savePendingPushVideo(payload) {
  if (!payload?.productId) return;
  try {
    sessionStorage.setItem(
      PUSH_PENDING_VIDEO_KEY,
      JSON.stringify({
        ...payload,
        ts: Date.now(),
      })
    );
  } catch (e) {
    console.warn('[PushNav] savePendingPushVideo failed', e);
  }
}

export function readPendingPushVideo() {
  try {
    const raw = sessionStorage.getItem(PUSH_PENDING_VIDEO_KEY);
    if (!raw) return null;
    return safeParseJson(raw);
  } catch (e) {
    return null;
  }
}

export function clearPendingPushVideo() {
  try {
    sessionStorage.removeItem(PUSH_PENDING_VIDEO_KEY);
  } catch (e) {
    // ignore
  }
}

export function savePendingPushSearch(payload) {
  if (!payload?.searchKeyword) return;
  try {
    sessionStorage.setItem(
      PUSH_PENDING_SEARCH_KEY,
      JSON.stringify({
        ...payload,
        ts: Date.now(),
      })
    );
  } catch (e) {
    console.warn('[PushNav] savePendingPushSearch failed', e);
  }
}

export function readPendingPushSearch() {
  try {
    const raw = sessionStorage.getItem(PUSH_PENDING_SEARCH_KEY);
    if (!raw) return null;
    return safeParseJson(raw);
  } catch (e) {
    return null;
  }
}

export function clearPendingPushSearch() {
  try {
    sessionStorage.removeItem(PUSH_PENDING_SEARCH_KEY);
  } catch (e) {
    // ignore
  }
}

export function buildReplenishmentSearchRoute(payload) {
  const keyword = payload?.searchKeyword;
  if (!keyword) return null;
  return {
    path: REPLENISHMENT_PATH,
    query: {
      search: String(keyword),
      fromPush: '1',
    },
  };
}

/**
 * 若当前已在配货页且页面已注册搜索器，直接填入搜索栏并搜索
 */
export function tryOpenSearchOnReplenishmentPage(payload) {
  const keyword = payload?.searchKeyword;
  if (!keyword) return false;

  if (typeof window === 'undefined' || window.__xpzReplenishmentPageActive !== true) {
    return false;
  }

  if (typeof window.__xpzOpenReplenishmentSearch === 'function') {
    window.__xpzOpenReplenishmentSearch(String(keyword));
    return true;
  }
  return false;
}

export function buildReplenishmentVideoRoute(payload) {
  const productId = payload?.productId || payload?.xpzVideoProductId;
  const videoIndex = payload?.videoIndex ?? payload?.xpzVideoIndex ?? 0;
  return {
    path: REPLENISHMENT_PATH,
    query: {
      xpzOpenVideo: '1',
      xpzVideoProductId: String(productId),
      xpzVideoIndex: String(videoIndex),
      fromPush: '1',
    },
  };
}

/**
 * 若当前已在配货页且页面已注册打开器，直接调 showVideoModal；否则返回 false 走路由
 */
export function tryOpenVideoOnReplenishmentPage(payload) {
  const productId = payload?.productId || payload?.xpzVideoProductId;
  if (!productId) return false;

  if (typeof window === 'undefined' || window.__xpzReplenishmentPageActive !== true) {
    return false;
  }

  const videoIndex = payload?.videoIndex ?? payload?.xpzVideoIndex ?? 0;
  if (typeof window.__xpzOpenReplenishmentVideo === 'function') {
    window.__xpzOpenReplenishmentVideo(String(productId), videoIndex);
    return true;
  }
  return false;
}

/**
 * 全局入口：原生 / 推送任意时刻可调用，内部会跳转配货页并打开视频
 */
export function installGlobalPushVideoBridge(router) {
  if (typeof window === 'undefined') return;

  window.__xpzNavigateReplenishmentVideo = (productId, videoIndex) => {
    if (!productId) return;
    const payload = {
      productId: String(productId),
      videoIndex: videoIndex ?? 0,
      xpzOpenVideo: '1',
      xpzVideoProductId: String(productId),
    };
    openReplenishmentVideoFromPush(payload, router).catch((e) => {
      console.warn('[PushNav] __xpzNavigateReplenishmentVideo failed', e);
    });
  };

  window.__xpzNavigateReplenishmentSearch = (searchKeyword) => {
    if (!searchKeyword) return;
    const payload = { searchKeyword: String(searchKeyword).trim() };
    openReplenishmentSearchFromPush(payload, router).catch((e) => {
      console.warn('[PushNav] __xpzNavigateReplenishmentSearch failed', e);
    });
  };
}

/** 等待配货页挂载后打开视频（不依赖搜索框 / 列表是否已有该商品） */
async function waitAndOpenReplenishmentVideo(payload, maxWaitMs = 25000) {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    if (tryOpenVideoOnReplenishmentPage(payload)) {
      clearPendingPushVideo();
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, 300));
  }
  console.warn('[PushNav] wait replenishment page timeout, pending kept', payload);
  return false;
}

/** 等待配货页挂载后执行搜索 */
async function waitAndOpenReplenishmentSearch(payload, maxWaitMs = 25000) {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    if (tryOpenSearchOnReplenishmentPage(payload)) {
      clearPendingPushSearch();
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, 300));
  }
  console.warn('[PushNav] wait replenishment search timeout, pending kept', payload);
  return false;
}

/**
 * 执行跳转并填入搜索栏自动搜索
 */
export async function openReplenishmentSearchFromPush(payload, router) {
  const keyword = payload?.searchKeyword ? String(payload.searchKeyword).trim() : '';
  if (!keyword) return false;

  const normalized = { searchKeyword: keyword };
  savePendingPushSearch(normalized);

  if (tryOpenSearchOnReplenishmentPage(normalized)) {
    clearPendingPushSearch();
    return true;
  }

  if (!router) {
    console.warn('[PushNav] router not ready, pending search saved');
    return false;
  }

  const route = buildReplenishmentSearchRoute(normalized);
  if (!route) return false;

  try {
    const current = router.currentRoute;
    const samePath = current && current.path === route.path;
    const sameSearch =
      samePath &&
      String(current.query?.search || '') === String(route.query.search);

    if (samePath && sameSearch) {
      tryOpenSearchOnReplenishmentPage(normalized);
      clearPendingPushSearch();
      return true;
    }

    await router.push(route);
    await waitAndOpenReplenishmentSearch(normalized);
    return true;
  } catch (e) {
    console.warn('[PushNav] router.push search failed, fallback hash', e);
    try {
      const q = route.query;
      const qs = Object.keys(q)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(q[k])}`)
        .join('&');
      window.location.hash = `#${route.path}?${qs}`;
      await waitAndOpenReplenishmentSearch(normalized);
      return true;
    } catch (e2) {
      console.error('[PushNav] search hash fallback failed', e2);
      return false;
    }
  }
}

/**
 * 执行跳转并尝试打开视频弹窗
 */
export async function openReplenishmentVideoFromPush(payload, router) {
  if (!payload?.productId && !payload?.xpzVideoProductId) {
    return false;
  }

  const normalized = {
    productId: String(payload.productId || payload.xpzVideoProductId),
    videoIndex: payload.videoIndex ?? payload.xpzVideoIndex ?? 0,
    xpzOpenVideo: '1',
    xpzVideoProductId: String(payload.productId || payload.xpzVideoProductId),
  };

  savePendingPushVideo(normalized);

  if (tryOpenVideoOnReplenishmentPage(normalized)) {
    clearPendingPushVideo();
    return true;
  }

  if (!router) {
    console.warn('[PushNav] router not ready, pending saved');
    return false;
  }

  const route = buildReplenishmentVideoRoute(normalized);
  try {
    const current = router.currentRoute;
    const samePath = current && current.path === route.path;
    const sameProduct =
      samePath &&
      String(current.query?.xpzVideoProductId || '') === String(route.query.xpzVideoProductId);

    if (samePath && sameProduct) {
      tryOpenVideoOnReplenishmentPage(normalized);
      clearPendingPushVideo();
      return true;
    }

    await router.push(route);
    await waitAndOpenReplenishmentVideo(normalized);
    return true;
  } catch (e) {
    console.warn('[PushNav] router.push failed, fallback hash', e);
    try {
      const q = route.query;
      const qs = Object.keys(q)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(q[k])}`)
        .join('&');
      window.location.hash = `#${route.path}?${qs}`;
      await waitAndOpenReplenishmentVideo(normalized);
      return true;
    } catch (e2) {
      console.error('[PushNav] hash fallback failed', e2);
      return false;
    }
  }
}

/**
 * JPush 点击通知（前台/后台 WebView 存活）
 */
export async function handlePushNotificationOpen(event, router) {
  const sources = collectAllPushSources(event);
  let payload = null;

  for (let i = 0; i < sources.length; i += 1) {
    payload = parsePushVideoPayload(sources[i]);
    if (payload) break;
  }

  if (payload) {
    console.log(`[PushNav] openNotification → replenishment video ${JSON.stringify(payload)}`);
    return openReplenishmentVideoFromPush(payload, router);
  }

  let searchPayload = null;
  for (let i = 0; i < sources.length; i += 1) {
    searchPayload = parsePushSearchPayload(sources[i]);
    if (searchPayload) break;
  }

  if (searchPayload) {
    console.log(`[PushNav] openNotification → replenishment search ${JSON.stringify(searchPayload)}`);
    return openReplenishmentSearchFromPush(searchPayload, router);
  }

  const debugSources = sources.map((item) => ({
    keys: item ? Object.keys(enumerateOwnProps(item)) : [],
    extra: item?.[JPUSH_EXTRA_KEY] ?? item?.extras ?? null,
    title: pickNotificationTitle(item),
    cached: !!getJPushCachedOpenNotification(),
  }));
  console.log(`[PushNav] openNotification: not a video/search push, ignored ${JSON.stringify(debugSources)}`);
  return false;
}

/**
 * App 启动 / 从原生 Intent 恢复后消费 pending
 */
export async function consumePendingPushNavigation(router) {
  const pendingVideo = readPendingPushVideo();
  if (pendingVideo?.productId || pendingVideo?.xpzVideoProductId) {
    console.log('[PushNav] consume pending push video', pendingVideo);
    const ok = await openReplenishmentVideoFromPush(pendingVideo, router);
    if (ok) {
      setTimeout(() => {
        tryOpenVideoOnReplenishmentPage(pendingVideo);
        clearPendingPushVideo();
      }, 800);
    }
    return ok;
  }

  const pendingSearch = readPendingPushSearch();
  if (pendingSearch?.searchKeyword) {
    console.log('[PushNav] consume pending push search', pendingSearch);
    const ok = await openReplenishmentSearchFromPush(pendingSearch, router);
    if (ok) {
      setTimeout(() => {
        tryOpenSearchOnReplenishmentPage(pendingSearch);
        clearPendingPushSearch();
      }, 800);
    }
    return ok;
  }

  return false;
}

/**
 * 注册全局监听（自定义事件 xpz-push-open，由 MainActivity 注入）
 */
export function bindPushNavigationListeners(router) {
  if (typeof window === 'undefined') return () => {};

  const onCustomEvent = (ev) => {
    const videoPayload = parsePushVideoPayload(ev) || parsePushVideoPayload(ev?.detail);
    if (videoPayload) {
      openReplenishmentVideoFromPush(videoPayload, router);
      return;
    }
    const searchPayload = parsePushSearchPayload(ev) || parsePushSearchPayload(ev?.detail);
    if (searchPayload) {
      openReplenishmentSearchFromPush(searchPayload, router);
    }
  };

  window.addEventListener('xpz-push-open', onCustomEvent);

  return () => {
    window.removeEventListener('xpz-push-open', onCustomEvent);
  };
}

export function initPushNavigation(router) {
  if (!Capacitor.isNativePlatform()) {
    return () => {};
  }

  installGlobalPushVideoBridge(router);
  const unbind = bindPushNavigationListeners(router);

  [600, 1500, 3000, 6000, 10000].forEach((delay) => {
    setTimeout(() => {
      consumePendingPushNavigation(router);
    }, delay);
  });

  document.addEventListener('resume', () => {
    setTimeout(() => consumePendingPushNavigation(router), 400);
  }, false);

  return unbind;
}

export default {
  parsePushVideoPayload,
  parsePushSearchPayload,
  handlePushNotificationOpen,
  openReplenishmentVideoFromPush,
  openReplenishmentSearchFromPush,
  consumePendingPushNavigation,
  installGlobalPushVideoBridge,
  initPushNavigation,
  readPendingPushSearch,
  clearPendingPushSearch,
};
